import React from 'react';
import {
	type AutoListCommonExtendProps,
	AutoListField,
} from '@netx/core-components/AutoListField';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {useAutoList} from '@netx/core-next/hooks/autoList/useAutoList';
import {splitEventHandler} from '@netx/core-next/utils/fn';
import {getUserAutoList} from '../../api';
import {UserPermission} from '../../constants';
import {UserSelectorLabel} from '../../locales';
import {type IUserAutoListItem} from '../../schema';
import {UserGroupListItem} from '../UserGroupListItem';

export {UserPermission} from '../../constants';
export {type IUserAutoListItem} from '../../schema';

export type UserAutoListRef = React.ForwardedRef<HTMLDivElement>;

const DEFAULT_NAME = 'user-auto-list';

export interface UserAutoListProps<
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
> extends AutoListCommonExtendProps<
	IUserAutoListItem,
	Multiple,
	DisableClearable,
	false
> {
	permission?: UserPermission;
}

/**
 * Default get option label fn
 * @param option Option
 */
const defaultGetOptionLabel = (option?: IUserAutoListItem) =>
	option?.name ?? '';

/**
 * Default get option selected fn
 * @param option Option
 * @param value Value
 */
const defaultGetOptionSelected = (
	option: IUserAutoListItem,
	value?: IUserAutoListItem,
) =>
	Array.isArray(value)
		? value.some((v) => v.id === option.id)
		: option.id === value?.id;

/** should option be disabled */
const defaultGetOptionDisabled = (option: IUserAutoListItem) => option.id === 0;

export const UserAutoListPrimitive = <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
>(
	{
		permission,
		filterOptions,
		getOptionLabel = defaultGetOptionLabel,
		getOptionSelected = defaultGetOptionSelected,
		getOptionDisabled = defaultGetOptionDisabled,
		onClose,
		onInputChange,
		onOpen,
		...props
	}: UserAutoListProps<Multiple, DisableClearable>,
	ref: UserAutoListRef,
) => {
	const {loadingOptions, query, queryStr, setQueryStr, setIsOpen} = useAutoList(
		{
			// we cannot utilize the `""` trick to get a list of results here
			isQueryValid: (query) => query !== '' && query !== '""',
			filterOptions,
			queryFn: getUserAutoList,
			params: {permission},
		},
	);

	const placeholder =
		props.multiple !== true ? UserSelectorLabel.SELECT_USER : undefined;

	return (
		<AutoListField<IUserAutoListItem, Multiple, DisableClearable>
			ref={ref}
			hasSecondaryText
			label={UserSelectorLabel.ADD_USER}
			loadingOptions={loadingOptions}
			multiple={false as Multiple}
			name={DEFAULT_NAME}
			placeholder={placeholder}
			filterOptions={filterOptions}
			isOptionEqualToValue={getOptionSelected}
			renderOption={(props, option, state) => (
				<UserGroupListItem
					label={option.name}
					secondaryLabel={option.email}
					{...props}
				/>
			)}
			testValueContainsInput={
				useConstCallback(
					(
						input?: IUserAutoListItem,
						value?: IUserAutoListItem | IUserAutoListItem[],
					) => {
						return Array.isArray(value)
							? value.some((v) => v.id === input?.id)
							: input?.id === value?.id;
					},
				) as any
			}
			testValueContainsOption={useConstCallback((option, value) => {
				return Array.isArray(value)
					? value.some((v) => v.value === option.value)
					: option.value === value?.value;
			})}
			inputValue={queryStr}
			{...props}
			options={query.data ?? []}
			getOptionDisabled={getOptionDisabled}
			getOptionLabel={getOptionLabel}
			onClose={splitEventHandler(onClose, () => setIsOpen(false))}
			onOpen={splitEventHandler(onOpen, () => setIsOpen(true))}
			onInputChange={splitEventHandler(
				onInputChange,
				(event, value, reason) => {
					setQueryStr(value);
				},
			)}
		/>
	);
};

/**
 * User auto list field
 */
export const UserAutoList = React.forwardRef(UserAutoListPrimitive) as <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
>(
	props: UserAutoListProps<Multiple, DisableClearable>,
) => JSX.Element;

export default UserAutoList;
